// Reclamo de aseguradora (auto-llenado del formulario oficial, script 395).
// Motor genérico: elige el formato (Aetna y otros), selecciona hasta N atenciones,
// previsualiza los datos resueltos (faltantes en rojo), completa/edita y genera
// el PDF llenado (overlay en el worker) que se guarda en la ficha del paciente.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Eye, FileText, Loader2, Plus, Save, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, EvolucionItem, Historia, MedicoRow, fieldCls, medRpc } from './shared';
import { CabeceraEx, BarraEx, Historial, VisorArchivo, fmtF, fmtFH } from './ExamenesFicha';
import type { ExamenRow } from './ExamenesFicha';

interface Formato { id: string; codigo: string; nombre: string; tratamientos_max: number }
interface Trat {
  fecha: string | null; proveedor: string | null; proveedor_direccion: string | null;
  proveedor_ciudad: string | null; proveedor_pais: string | null;
  costo: string | null; moneda: string | null; razon_visita: string | null; descripcion: string | null;
  internado: boolean; fecha_admision: string | null; fecha_alta: string | null;
  costo_origen?: string | null; costo_ref?: string | null;
}
interface DatosResueltos { valores: Record<string, string | null>; tratamientos: Trat[]; constantes: Record<string, string>; codigo: string; nombre: string }
interface ReclamoRow { id: string; numero: number | null; fecha: string; datos: Record<string, unknown> }

// Campos del perfil de aseguradora del paciente que persisten entre reclamos
const PERFIL = [
  ['perfil.numero_asegurado', 'N° de asegurado', true],
  ['perfil.mision_grupo', 'Nombre del grupo', false],
  ['perfil.mision_num', 'Número de la misión', false],
  ['perfil.mision_tel', 'Teléfono de la misión', false],
  ['perfil.mision_email', 'Email de la misión', false],
] as const;
const DEMO = [
  ['paciente.nombres', 'Nombres', true], ['paciente.apellidos', 'Apellidos', true],
  ['paciente.genero', 'Sexo (M/F)', false], ['paciente.direccion', 'Dirección', false],
  ['paciente.ciudad', 'Ciudad', false], ['paciente.pais', 'País', false],
] as const;

// --- Gate "reclamo listo": lo que debe estar completo antes de generar el PDF ---
interface Chk { clave: string; label: string; ok: boolean; req: boolean; hint?: string }
interface ChkAtencion { id: string; fecha: string | null; resumen: string; items: Chk[]; listo: boolean }
const soloDig = (s?: string | null) => (s ?? '').replace(/\D/g, '');
// Qué le falta a una atención para poder reclamarla (badge en la lista de selección)
function faltantesAtencion(ev: EvolucionItem): string[] {
  const m: string[] = [];
  if (!ev.evolucion.motivo && ev.diagnosticos.length === 0) m.push('motivo');
  if (!ev.diagnosticos.some((d) => d.cie10_codigo)) m.push('CIE-10');
  if (!(Number((ev.evolucion.datos as Record<string, unknown> | null)?.costo_total) > 0)) m.push('costo');
  return m;
}
function GateRow({ c }: { c: Chk }) {
  return (
    <li className="flex items-start gap-1.5">
      {c.ok
        ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
        : <AlertCircle className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${c.req ? 'text-red-500' : 'text-amber-500'}`} />}
      <span className={c.ok ? 'text-muted-foreground' : c.req ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}>
        {c.label}{!c.req && ' (opcional)'}{!c.ok && c.hint ? <span className="text-muted-foreground"> — {c.hint}</span> : null}
      </span>
    </li>
  );
}

export function ReclamoAseguradora({ pacienteId, onVolver }: { pacienteId: string; onVolver: () => void }) {
  const qc = useQueryClient();
  const { data: formatos = [] } = useQuery<Formato[]>({
    queryKey: ['med-aseg-formatos'], queryFn: () => medRpc<Formato[]>('med_aseguradora_formatos', {}),
  });
  const { data: reclamos = [], isLoading } = useQuery<ReclamoRow[]>({
    queryKey: ['med-ficha', pacienteId, 'reclamos'],
    queryFn: () => medRpc<ReclamoRow[]>('med_examenes_ficha', { _paciente_id: pacienteId, _categoria: 'reclamo' }),
  });
  const { data: his } = useQuery<Historia>({
    queryKey: ['med-ficha', pacienteId, 'historia'], queryFn: () => medRpc<Historia>('med_historia', { _paciente_id: pacienteId }),
  });
  const { data: medicos = [] } = useQuery<MedicoRow[]>({ queryKey: ['med-medicos', ''], queryFn: () => medRpc<MedicoRow[]>('med_medicos', { _busqueda: '' }) });

  const [form, setForm] = useState(false);
  const [selId, setSelId] = useState<string | null>(null);
  const sel = reclamos.find((r) => r.id === selId) ?? null;

  if (isLoading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  return (
    <div className="space-y-3">
      <CabeceraEx titulo="Reclamos de aseguradora" onVolver={onVolver} />
      <section>
        <BarraEx titulo="Reclamos de aseguradora"
          accion={!form && <Button size="sm" onClick={() => { setForm(true); setSelId(null); }} disabled={formatos.length === 0}><Plus className="w-4 h-4 mr-1" /> Nuevo reclamo</Button>} />
        <div className="rounded-b-md border border-t-0 bg-card p-4">
          {formatos.length === 0 && !form && (
            <p className="py-6 text-center text-sm text-amber-600">No hay formatos de aseguradora configurados.</p>
          )}
          <div className="flex gap-4 items-start">
            <Historial rows={reclamos.map((r) => ({ ...r, categoria: 'reclamo', datos: r.datos, created_at: r.fecha, n_archivos: 1 })) as unknown as ExamenRow[]}
              selId={sel?.id ?? null} onSel={(id) => { setSelId(id); setForm(false); }}
              etiqueta={(r) => `#${(r as unknown as ReclamoRow).numero ?? ''} · ${String((r as unknown as ReclamoRow).datos?.aseguradora ?? '')}`} />
            <div className="flex-1 min-w-0 text-sm">
              {form ? (
                <FormReclamo pacienteId={pacienteId} formatos={formatos}
                  evoluciones={his?.evoluciones ?? []} medicos={medicos}
                  onCancel={() => setForm(false)}
                  onCreado={() => { setForm(false); void qc.invalidateQueries({ queryKey: ['med-ficha', pacienteId, 'reclamos'] }); }} />
              ) : !sel ? (
                <p className="py-10 text-center text-primary/80">Selecciona un reclamo o crea uno nuevo.</p>
              ) : (
                <VistaReclamo reclamo={sel} pacienteId={pacienteId} />
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---- Formulario de nuevo reclamo ----
function FormReclamo({ pacienteId, formatos, evoluciones, medicos, onCancel, onCreado }: {
  pacienteId: string; formatos: Formato[]; evoluciones: EvolucionItem[]; medicos: MedicoRow[];
  onCancel: () => void; onCreado: () => void;
}) {
  const qc = useQueryClient();
  const [formatoId, setFormatoId] = useState(formatos[0]?.id ?? '');
  const [medicoId, setMedicoId] = useState('');
  // El médico responsable es el usuario logueado (no se elige). Admin/recepción mantiene el selector.
  const { data: miMedico } = useQuery<{ id: string; razon_social: string } | null>({
    queryKey: ['med-mi-medico'], queryFn: () => medRpc<{ id: string; razon_social: string } | null>('med_mi_medico'),
  });
  const miMedicoId = miMedico?.id ?? '';
  useEffect(() => { if (miMedicoId) setMedicoId(miMedicoId); }, [miMedicoId]);
  const [sel, setSel] = useState<string[]>([]);           // evolucion ids
  const [datos, setDatos] = useState<DatosResueltos | null>(null);
  const [srv, setSrv] = useState('medico');               // tipo de servicio
  const [accLaboral, setAccLaboral] = useState(false);
  const [otraCob, setOtraCob] = useState(false);
  const formato = formatos.find((f) => f.id === formatoId);
  const maxT = formato?.tratamientos_max ?? 4;

  const toggle = (id: string) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length < maxT ? [...s, id] : s));

  const resolver = useMutation({
    mutationFn: () => medRpc<DatosResueltos>('med_reclamo_aseguradora_datos', { _paciente_id: pacienteId, _plantilla_id: formatoId, _atenciones: sel }),
    onSuccess: (d) => setDatos(d),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const setVal = (k: string, v: string) => setDatos((d) => (d ? { ...d, valores: { ...d.valores, [k]: v } } : d));
  const setTrat = (i: number, patch: Partial<Trat>) => setDatos((d) => (d ? { ...d, tratamientos: d.tratamientos.map((t, j) => (j === i ? { ...t, ...patch } : t)) } : d));

  const guardarPerfil = useMutation({
    mutationFn: () => medRpc('med_paciente_aseguradora_set', {
      _paciente_id: pacienteId, _codigo: datos!.codigo,
      _perfil: Object.fromEntries(PERFIL.map(([k]) => [k.replace('perfil.', ''), datos!.valores[k] ?? ''])),
    }),
    onSuccess: () => toast.success('Perfil de aseguradora guardado en el paciente'),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const crear = useMutation({
    mutationFn: () => {
      const valores = { ...datos!.valores, 'reclamo.tipo_servicio': srv, 'reclamo.accidente_laboral': accLaboral, 'reclamo.otra_cobertura': otraCob };
      return medRpc<{ reclamo_id: string }>('med_reclamo_aseguradora_crear', {
        _paciente_id: pacienteId, _plantilla_id: formatoId,
        _valores: { valores, tratamientos: datos!.tratamientos }, _atenciones: sel, _medico_id: (miMedicoId || medicoId) || null,
      });
    },
    onSuccess: () => { toast.success('Reclamo creado — el PDF se está generando'); void qc.invalidateQueries({ queryKey: ['med-ficha', pacienteId, 'reclamos'] }); onCreado(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const check = useMemo(() => {
    if (!datos) return null;
    const v = datos.valores;
    const aseg: Chk[] = [
      { clave: 'nombres', label: 'Nombres', ok: !!v['paciente.nombres'], req: true },
      { clave: 'apellidos', label: 'Apellidos', ok: !!v['paciente.apellidos'], req: true },
      { clave: 'num', label: 'N° de asegurado', ok: soloDig(v['perfil.numero_asegurado']).length >= 8, req: true, hint: 'Guárdalo en el perfil de la aseguradora' },
    ];
    const ats: ChkAtencion[] = sel.map((id, i) => {
      const ev = evoluciones.find((e) => e.evolucion.id === id);
      const t = datos.tratamientos[i];
      const items: Chk[] = [
        { clave: 'razon', label: 'Razón de la visita', ok: !!t?.razon_visita, req: true, hint: 'Registra motivo o diagnóstico en la consulta' },
        { clave: 'dx', label: 'Diagnóstico CIE-10', ok: !!ev?.diagnosticos.some((d) => d.cie10_codigo), req: srv !== 'farmacia', hint: 'Agrégalo en Enfermedad actual de la ficha' },
        { clave: 'costo', label: 'Costo total', ok: Number(t?.costo) > 0, req: true, hint: 'Ingresa el costo de la atención' },
      ];
      if (t?.internado) {
        items.push({ clave: 'adm', label: 'Fecha de admisión', ok: !!t?.fecha_admision, req: true });
        items.push({ clave: 'alta', label: 'Fecha de alta', ok: !!t?.fecha_alta, req: true });
      }
      const listo = items.filter((x) => x.req).every((x) => x.ok);
      return { id, fecha: ev?.evolucion.fecha ?? t?.fecha ?? null, resumen: ev?.evolucion.motivo || ev?.diagnosticos[0]?.descripcion || 'Consulta', items, listo };
    });
    const reqFail = aseg.filter((x) => x.req && !x.ok).length + ats.reduce((n, a) => n + a.items.filter((x) => x.req && !x.ok).length, 0);
    return { aseg, ats, listo: reqFail === 0 && ats.length > 0, faltan: reqFail };
  }, [datos, sel, evoluciones, srv]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Campo label="Formato de aseguradora *">
          <select className={fieldCls} value={formatoId} onChange={(e) => { setFormatoId(e.target.value); setDatos(null); setSel([]); }}>
            {formatos.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
          </select>
        </Campo>
        <Campo label="Médico responsable">
          {miMedicoId ? (
            <input className={fieldCls} value={miMedico?.razon_social ?? ''} readOnly disabled title="Se toma del médico que ha iniciado sesión" />
          ) : (
            <select className={fieldCls} value={medicoId} onChange={(e) => setMedicoId(e.target.value)}>
              <option value="">— Opcional —</option>{medicos.map((m) => <option key={m.id} value={m.id}>{m.razon_social}</option>)}
            </select>
          )}
        </Campo>
      </div>

      {/* Selección de atenciones (evoluciones) a reclamar */}
      <div>
        <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Atenciones a reclamar (máx {maxT})</p>
        <div className="max-h-40 overflow-y-auto rounded-md border divide-y">
          {evoluciones.length === 0 && <p className="p-3 text-xs text-muted-foreground">El paciente no tiene consultas registradas.</p>}
          {evoluciones.map((ev) => (
            <label key={ev.evolucion.id} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-muted/40">
              <input type="checkbox" checked={sel.includes(ev.evolucion.id)} onChange={() => toggle(ev.evolucion.id)} />
              {(() => { const m = faltantesAtencion(ev); return (
                <span title={m.length ? `Falta para reclamar: ${m.join(', ')}` : 'Datos completos para reclamar'}
                  className={`h-2 w-2 shrink-0 rounded-full ${m.length ? 'bg-amber-400' : 'bg-emerald-500'}`} />
              ); })()}
              <span className="text-muted-foreground">{fmtF(ev.evolucion.fecha)}</span>
              <span className="truncate">{ev.evolucion.motivo || ev.diagnosticos[0]?.descripcion || 'Consulta'}</span>
              <span className="ml-auto text-xs text-muted-foreground">{ev.medico}</span>
            </label>
          ))}
        </div>
        <div className="mt-2">
          <Button size="sm" variant="outline" disabled={sel.length === 0} isLoading={resolver.isPending} onClick={() => resolver.mutate()}>
            Resolver datos ({sel.length})
          </Button>
        </div>
      </div>

      {datos && (
        <div className="space-y-4 border-t pt-3">
          {/* Gate "reclamo listo": guardarraíl antes de generar el PDF */}
          {check && (
            <div className={`rounded-md border px-3 py-2 text-xs ${check.listo ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-900/20' : 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20'}`}>
              <p className={`mb-1.5 flex items-center gap-1.5 font-semibold ${check.listo ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-800 dark:text-amber-300'}`}>
                {check.listo
                  ? <><CheckCircle2 className="h-4 w-4" /> Reclamo listo para generar</>
                  : <><AlertCircle className="h-4 w-4" /> Faltan {check.faltan} dato(s) obligatorio(s) para generar</>}
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                <div>
                  <p className="mb-0.5 font-medium text-muted-foreground">Asegurado</p>
                  <ul className="space-y-0.5">{check.aseg.map((c) => <GateRow key={c.clave} c={c} />)}</ul>
                </div>
                {check.ats.map((a, i) => (
                  <div key={a.id}>
                    <p className="mb-0.5 flex items-center gap-1 font-medium text-muted-foreground">
                      {a.listo ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <AlertCircle className="h-3.5 w-3.5 text-amber-500" />}
                      Atención {i + 1}{a.fecha ? ` · ${fmtF(a.fecha)}` : ''}
                    </p>
                    <ul className="space-y-0.5">{a.items.map((c) => <GateRow key={c.clave} c={c} />)}</ul>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Datos del paciente + perfil */}
          <div>
            <div className="mb-1 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase text-muted-foreground">Paciente y aseguradora</p>
              <Button size="sm" variant="outline" isLoading={guardarPerfil.isPending} onClick={() => guardarPerfil.mutate()}>
                <Save className="w-3.5 h-3.5 mr-1" /> Guardar perfil al paciente
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[...DEMO, ...PERFIL].map(([k, l, req]) => (
                <Campo key={k} label={`${l}${req ? ' *' : ''}`}>
                  <input className={`${fieldCls} ${req && !datos.valores[k] ? 'border-red-400' : ''}`}
                    value={datos.valores[k] ?? ''} onChange={(e) => setVal(k, e.target.value)} />
                </Campo>
              ))}
            </div>
          </div>

          {/* Opciones del reclamo */}
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <label className="flex items-center gap-1">Servicio:
              <select className={`${fieldCls} h-8 w-auto`} value={srv} onChange={(e) => setSrv(e.target.value)}>
                <option value="medico">Médico</option><option value="farmacia">Farmacia</option>
              </select>
            </label>
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={accLaboral} onChange={(e) => setAccLaboral(e.target.checked)} /> Accidente/enfermedad laboral</label>
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={otraCob} onChange={(e) => setOtraCob(e.target.checked)} /> Tiene otra cobertura de salud</label>
          </div>

          {/* Tratamientos */}
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase text-muted-foreground">Tratamientos ({datos.tratamientos.length})</p>
            {datos.tratamientos.map((t, i) => (
              <div key={i} className="rounded-md border p-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Campo label="Fecha servicio"><input type="date" className={fieldCls} value={t.fecha ?? ''} onChange={(e) => setTrat(i, { fecha: e.target.value })} /></Campo>
                <Campo label="Costo">
                  <input className={fieldCls} value={t.costo ?? ''} onChange={(e) => setTrat(i, { costo: e.target.value, costo_origen: 'manual', costo_ref: null })} />
                  {t.costo_origen && t.costo_origen !== 'manual' && (
                    <span className="mt-0.5 block text-[10px] text-emerald-600 dark:text-emerald-400" title={t.costo_ref ?? ''}>
                      ✓ Desde {t.costo_origen === 'pos' ? 'venta POS' : t.costo_origen === 'factura+pos' ? 'factura + POS' : 'factura'}{t.costo_ref ? ` ${t.costo_ref}` : ''}
                    </span>
                  )}
                </Campo>
                <Campo label="Moneda"><input className={fieldCls} value={t.moneda ?? ''} onChange={(e) => setTrat(i, { moneda: e.target.value })} /></Campo>
                <label className="flex items-end gap-1.5 pb-2 text-sm"><input type="checkbox" checked={t.internado} onChange={(e) => setTrat(i, { internado: e.target.checked })} /> Internado</label>
                <Campo label="Razón de la visita" className="sm:col-span-2"><input className={fieldCls} value={t.razon_visita ?? ''} onChange={(e) => setTrat(i, { razon_visita: e.target.value })} /></Campo>
                <Campo label="Descripción de servicios" className="sm:col-span-2"><input className={fieldCls} value={t.descripcion ?? ''} onChange={(e) => setTrat(i, { descripcion: e.target.value })} /></Campo>
                {t.internado && (<>
                  <Campo label="Admisión"><input type="date" className={fieldCls} value={t.fecha_admision ?? ''} onChange={(e) => setTrat(i, { fecha_admision: e.target.value })} /></Campo>
                  <Campo label="Alta"><input type="date" className={fieldCls} value={t.fecha_alta ?? ''} onChange={(e) => setTrat(i, { fecha_alta: e.target.value })} /></Campo>
                </>)}
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 border-t pt-3">
            <Button size="sm" isLoading={crear.isPending} disabled={!check?.listo} onClick={() => crear.mutate()}><Send className="w-4 h-4 mr-1" /> Generar reclamo (PDF)</Button>
            <Button size="sm" variant="outline" onClick={onCancel}>Cancelar</Button>
            {!check?.listo && <span className="text-xs text-amber-600 dark:text-amber-400">Completa los datos obligatorios para habilitar la generación.</span>}
          </div>
          <p className="text-xs text-muted-foreground">El PDF se genera con el formato oficial de la aseguradora. La firma del paciente/autorizado se hace sobre el impreso.</p>
        </div>
      )}
      {!datos && <div className="flex gap-2"><Button size="sm" variant="outline" onClick={onCancel}>Cancelar</Button></div>}
    </div>
  );
}

// ---- Vista de un reclamo generado (estado + descarga del PDF) ----
function VistaReclamo({ reclamo, pacienteId }: { reclamo: ReclamoRow; pacienteId: string }) {
  const [visto, setVisto] = useState<{ id: string; nombre: string; archivo: string } | null>(null);
  const { data: est } = useQuery<{ estado: string; mensaje: string | null; archivo_id: string | null }>({
    queryKey: ['med-reclamo-estado', reclamo.id],
    queryFn: () => medRpc('med_reclamo_estado', { _id: reclamo.id }),
    refetchInterval: (q) => (['generado', 'error'].includes(q.state.data?.estado ?? '') ? false : 3000),
  });
  const { data: archivos = [] } = useQuery<{ id: string; nombre: string; archivo: string }[]>({
    queryKey: ['med-ficha', pacienteId, 'reclamo-archivo', reclamo.id],
    queryFn: () => medRpc<{ id: string; nombre: string; archivo: string }[]>('med_examen_archivos', { _examen_id: reclamo.id }),
    enabled: est?.estado === 'generado',
  });
  const d = reclamo.datos;
  const estado = est?.estado ?? String(d.estado ?? '');
  const LBL: Record<string, { t: string; cls: string }> = {
    encolado: { t: 'Generando…', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
    procesando: { t: 'Generando…', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
    generado: { t: 'PDF listo', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
    error: { t: 'Error', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  };
  const e = LBL[estado] ?? LBL.encolado;
  return (
    <div className="space-y-3">
      <p className="font-semibold">Reclamo #{reclamo.numero} <span className="font-normal text-muted-foreground">· {String(d.aseguradora ?? '')} · {fmtFH(reclamo.fecha)}</span></p>
      <div className="flex items-center gap-2">
        <span className={`rounded px-2 py-0.5 text-xs ${e.cls} ${['encolado', 'procesando'].includes(estado) ? 'animate-pulse' : ''}`}>{e.t}</span>
        {estado === 'error' && <span className="text-xs text-red-600">{est?.mensaje}</span>}
      </div>
      {estado === 'generado' && archivos.length > 0 && (
        <div className="space-y-1.5">
          {archivos.map((f) => (
            <div key={f.id} className="flex flex-wrap items-center gap-2">
              <a href={f.archivo} download={f.nombre} className="flex w-fit items-center gap-1.5 rounded-md border px-3 py-2 text-sm text-primary hover:bg-muted">
                <FileText className="w-4 h-4" /> {f.nombre.startsWith('Superbill') ? 'Descargar superbill (detalle itemizado)' : 'Descargar formulario del reclamo'}
              </a>
              <button type="button" className="flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm text-primary hover:bg-muted" onClick={() => setVisto(visto?.id === f.id ? null : f)}>
                <Eye className="w-4 h-4" /> Ver
              </button>
            </div>
          ))}
          {visto && <VisorArchivo a={{ nombre: visto.nombre, mime: null, archivo: visto.archivo }} onClose={() => setVisto(null)} />}
        </div>
      )}
      {estado === 'generado' && archivos.length === 0 && <p className="text-xs text-muted-foreground">Cargando los PDF…</p>}
    </div>
  );
}
